/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Typography, Row, Col, Spin, DatePicker, Table } from 'antd';
import { Button as CrcButton } from '@crc-evans/ui-components';
import { DashboardOutlined, LineChartOutlined, SafetyOutlined, CheckCircleOutlined, BarChartOutlined, AppstoreOutlined, TeamOutlined, ShoppingOutlined, WarningOutlined, LoadingOutlined, PieChartOutlined } from '@ant-design/icons';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { ChevronDown, ChevronUp, SlidersHorizontal, FileText, FileSpreadsheet, Maximize2, Minimize2, BarChart2, LayoutList } from 'lucide-react';
import { Toggle } from '@crc-evans/ui-components';
import axiosConfig from '../../services/axiosConfig';
import moment from 'moment';

const { Title } = Typography;
const { RangePicker } = DatePicker;

// Reusable single-select dropdown (same as ConsumablesLog / PersonnelLog)
const CustomSingleSelect = ({ value, options = [], onChange, placeholder = 'Select...', disabled = false, valueKey, labelKey, openUp = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const opts = Array.isArray(options) ? options : [];
  const displayLabel = value || '';

  const getOptValue = (opt) => {
    if (typeof opt === 'string') return opt;
    if (valueKey && opt[valueKey] != null) return String(opt[valueKey]);
    return opt.value ?? opt.label ?? opt.name ?? '';
  };
  const getOptLabel = (opt) => {
    if (typeof opt === 'string') return opt;
    if (labelKey && opt[labelKey] != null) return String(opt[labelKey]);
    return opt.label ?? opt.name ?? opt.value ?? '';
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', minWidth: 0, textAlign: 'left' }}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          minWidth: '140px',
          minHeight: '36px',
          height: '36px',
          boxSizing: 'border-box',
          padding: '4px 10px',
          fontSize: '13px',
          border: isOpen ? '2px solid #3B82F6' : '1px solid #E5E7EB',
          borderRadius: '6px',
          backgroundColor: disabled ? '#F9FAFB' : '#fff',
          color: displayLabel ? '#374151' : '#9CA3AF',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '6px',
          transition: 'border-color 0.2s',
          opacity: disabled ? 0.6 : 1,
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayLabel || placeholder}
        </span>
        {isOpen ? <ChevronUp size={14} color="#6B7280" /> : <ChevronDown size={14} color="#6B7280" />}
      </div>

      {isOpen && !disabled && (
        <div style={{
          position: 'absolute',
          ...(openUp ? { bottom: 'calc(100% + 4px)', top: 'auto' } : { top: 'calc(100% + 4px)' }),
          left: 0,
          zIndex: 9999,
          backgroundColor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          padding: '6px',
          minWidth: '140px',
          maxHeight: '220px',
          overflowY: 'auto',
        }}>
          {opts.map((opt, index) => {
            const optValue = getOptValue(opt);
            const optLabel = getOptLabel(opt);
            return (
              <div
                key={index}
                onClick={() => { onChange(optValue); setIsOpen(false); }}
                style={{
                  padding: '7px 10px',
                  fontSize: '13px',
                  color: '#374151',
                  backgroundColor: value === optValue ? '#F3F4F6' : 'transparent',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F9FAFB'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = value === optValue ? '#F3F4F6' : 'transparent'; }}
              >
                {optLabel}
              </div>
            );
          })}
          {opts.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: '13px', color: '#9CA3AF' }}>No options</div>
          )}
        </div>
      )}
    </div>
  );
};

// Multi-select dropdown for Mainline filter
const CustomMultiSelect = ({ values = [], options = [], onChange, placeholder = 'Select...', disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const opts = Array.isArray(options) ? options : [];
  const selected = Array.isArray(values) ? values : [];

  const displayText = selected.length === 0
    ? placeholder
    : selected.length === opts.length
      ? 'All Mainlines'
      : selected.length === 1
        ? (opts.find(o => o.value === selected[0])?.label || `${selected.length} selected`)
        : `${selected.length} selected`;

  const toggleOption = (val) => {
    const next = selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val];
    onChange(next);
  };

  const checkboxStyle = (active) => ({
    width: '15px', height: '15px', borderRadius: '3px', flexShrink: 0,
    border: active ? '2px solid #3B82F6' : '1px solid #D1D5DB',
    backgroundColor: active ? '#3B82F6' : '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  });

  return (
    <div ref={containerRef} style={{ position: 'relative', minWidth: 0, textAlign: 'left' }}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          minWidth: '200px', minHeight: '36px', height: '36px', boxSizing: 'border-box',
          padding: '4px 10px', fontSize: '13px',
          border: isOpen ? '2px solid #3B82F6' : '1px solid #E5E7EB',
          borderRadius: '6px', backgroundColor: disabled ? '#F9FAFB' : '#fff',
          color: selected.length > 0 ? '#374151' : '#9CA3AF',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px',
          transition: 'border-color 0.2s', opacity: disabled ? 0.6 : 1,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {displayText}
        </span>
        {selected.length > 0 && (
          <span style={{ backgroundColor: '#3B82F6', color: '#fff', borderRadius: '10px', fontSize: '11px', fontWeight: 700, padding: '1px 6px', flexShrink: 0 }}>
            {selected.length}
          </span>
        )}
        {isOpen ? <ChevronUp size={14} color="#6B7280" /> : <ChevronDown size={14} color="#6B7280" />}
      </div>

      {isOpen && !disabled && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 9999,
          backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.14)', padding: '6px',
          minWidth: '220px', maxHeight: '280px', overflowY: 'auto',
        }}>
          {/* All / clear row */}
          <div
            onClick={() => onChange([])}
            style={{
              padding: '7px 10px', fontSize: '13px', cursor: 'pointer', borderRadius: '4px',
              marginBottom: '4px', borderBottom: '1px solid #F3F4F6',
              display: 'flex', alignItems: 'center', gap: '8px',
              backgroundColor: selected.length === 0 ? '#EFF6FF' : 'transparent',
              color: selected.length === 0 ? '#1d4ed8' : '#374151',
              fontWeight: selected.length === 0 ? 600 : 400,
            }}
          >
            <span style={checkboxStyle(selected.length === 0)}>
              {selected.length === 0 && <span style={{ color: '#fff', fontSize: '10px', lineHeight: 1 }}>✓</span>}
            </span>
            All Mainlines
          </div>
          {opts.map((opt, i) => {
            const isActive = selected.includes(opt.value);
            return (
              <div
                key={i}
                onClick={() => toggleOption(opt.value)}
                style={{
                  padding: '7px 10px', fontSize: '13px', cursor: 'pointer', borderRadius: '4px',
                  marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '8px',
                  backgroundColor: isActive ? '#EFF6FF' : 'transparent', color: '#374151',
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = '#F9FAFB'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <span style={checkboxStyle(isActive)}>
                  {isActive && <span style={{ color: '#fff', fontSize: '10px', lineHeight: 1 }}>✓</span>}
                </span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.label}</span>
              </div>
            );
          })}
          {opts.length === 0 && <div style={{ padding: '8px 10px', fontSize: '13px', color: '#9CA3AF' }}>No options</div>}
        </div>
      )}
    </div>
  );
};

// ========== CHART COMPONENTS ==========

const DefectTrendChart = ({ data = [], dataSource, dateRangeLabel }) => {
  const sampleData = [
    { date: 'Feb 3', porosity: 3, lof: 1, slag: 2, cracks: 0, ip: 1 },
    { date: 'Feb 4', porosity: 4, lof: 2, slag: 1, cracks: 0, ip: 2 },
    { date: 'Feb 5', porosity: 2, lof: 1, slag: 3, cracks: 1, ip: 0 },
    { date: 'Feb 6', porosity: 5, lof: 3, slag: 2, cracks: 0, ip: 1 },
    { date: 'Feb 7', porosity: 3, lof: 2, slag: 1, cracks: 0, ip: 2 },
    { date: 'Feb 8', porosity: 4, lof: 1, slag: 2, cracks: 1, ip: 1 },
    { date: 'Feb 9', porosity: 2, lof: 2, slag: 3, cracks: 0, ip: 0 }
  ];
  const chartData = data.length > 0 ? data : sampleData;
  const rangeText = dateRangeLabel || 'Selected date range';
  return (
    <Card className="dashboard-card" style={{ marginBottom: 0, height: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: 'rgba(255, 77, 79, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LineChartOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
        </div>
        <div>
          <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>Defect Trends ({rangeText})</Title>
          {dataSource && <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '11px', marginTop: '4px' }}>From: {dataSource}</div>}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
          <XAxis dataKey="date" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} />
          <YAxis stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} />
          <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
          <Legend wrapperStyle={{ color: '#fff', paddingTop: '20px' }} />
          <Line type="monotone" dataKey="porosity" stroke="#8884d8" strokeWidth={2} name="Porosity" dot={{ fill: '#8884d8', r: 4 }} />
          <Line type="monotone" dataKey="lof" stroke="#82ca9d" strokeWidth={2} name="Lack of Fusion" dot={{ fill: '#82ca9d', r: 4 }} />
          <Line type="monotone" dataKey="slag" stroke="#ffc658" strokeWidth={2} name="Slag Inclusion" dot={{ fill: '#ffc658', r: 4 }} />
          <Line type="monotone" dataKey="cracks" stroke="#ff4d4f" strokeWidth={2} name="Cracks" dot={{ fill: '#ff4d4f', r: 4 }} />
          <Line type="monotone" dataKey="ip" stroke="#a4de6c" strokeWidth={2} name="Incomplete Penetration" dot={{ fill: '#a4de6c', r: 4 }} />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};

const AcceptanceRateChart = ({ data = [], dataSource }) => {
  const sampleData = [
    { date: 'Mon', rtRate: 92, utRate: 95 },
    { date: 'Tue', rtRate: 88, utRate: 91 },
    { date: 'Wed', rtRate: 91, utRate: 94 },
    { date: 'Thu', rtRate: 93, utRate: 96 },
    { date: 'Fri', rtRate: 89, utRate: 92 },
    { date: 'Sat', rtRate: 94, utRate: 97 },
    { date: 'Sun', rtRate: 91, utRate: 93 }
  ];
  const chartData = data.length > 0 ? data : sampleData;
  return (
    <Card className="dashboard-card" style={{ marginBottom: 0, height: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: 'rgba(82, 196, 26, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
        </div>
        <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>Acceptance Rate Trends</Title>
        {dataSource && <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '11px', marginTop: '4px' }}>From: {dataSource}</div>}
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="colorRt" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#52c41a" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#52c41a" stopOpacity={0.1} />
            </linearGradient>
            <linearGradient id="colorUt" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#1890ff" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#1890ff" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
          <XAxis dataKey="date" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} />
          <YAxis stroke="rgba(255,255,255,0.65)" domain={[0, 100]} tickFormatter={(value) => `${value}%`} style={{ fontSize: '12px' }} />
          <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} formatter={(value) => `${value}%`} />
          <Legend wrapperStyle={{ paddingTop: '20px' }} />
          <Area type="monotone" dataKey="rtRate" stroke="#52c41a" strokeWidth={2} fill="url(#colorRt)" name="RT Acceptance Rate" dot={{ fill: '#52c41a', r: 4 }} />
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  );
};

const WelderPerformanceChart = ({ data = [], dataSource }) => {
  const sampleData = [
    { welder: 'Abijith', accepted: 46, rejected: 2 },
    { welder: 'Devaraj', accepted: 31, rejected: 2 },
    { welder: 'Madhaudhan', accepted: 30, rejected: 1 },
    { welder: 'Kumar', accepted: 28, rejected: 3 },
    { welder: 'Ravi', accepted: 25, rejected: 1 }
  ];
  const chartData = data.length > 0 ? data : sampleData;
  return (
    <Card className="dashboard-card" style={{ marginBottom: 0, height: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: 'rgba(114, 46, 209, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <TeamOutlined style={{ color: '#722ed1', fontSize: '20px' }} />
        </div>
        <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>Welder Performance</Title>
        {dataSource && <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '11px', marginTop: '4px' }}>From: {dataSource}</div>}
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
          <XAxis dataKey="welder" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} angle={-45} textAnchor="end" height={80} />
          <YAxis stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} />
          <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
          <Legend wrapperStyle={{ paddingTop: '20px' }} />
          <Bar dataKey="accepted" fill="#52c41a" name="Accepted" radius={[8, 8, 0, 0]} />
          <Bar dataKey="rejected" fill="#ff4d4f" name="Rejected" radius={[8, 8, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
};

const DefectDistributionChart = ({ data = [], dataSource }) => {
  const sampleData = [
    { name: 'Porosity', value: 156 },
    { name: 'Slag Inclusion', value: 112 },
    { name: 'Lack of Fusion', value: 67 },
    { name: 'Incomplete Penetration', value: 43 },
    { name: 'Cracks', value: 15 }
  ];
  const chartData = data.length > 0 ? data : sampleData;
  const COLORS = ['#1890ff', '#faad14', '#52c41a', '#ff4d4f', '#722ed1'];
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" style={{ fontSize: '14px', fontWeight: 600 }}>
        {`${(percent * 100).toFixed(1)}%`}
      </text>
    );
  };
  return (
    <Card className="dashboard-card" style={{ marginBottom: 0, height: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: 'rgba(250, 173, 20, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PieChartOutlined style={{ color: '#faad14', fontSize: '20px' }} />
        </div>
        <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>Defect Distribution</Title>
        {dataSource && <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '11px', marginTop: '4px' }}>From: {dataSource}</div>}
      </div>
      <ResponsiveContainer width="100%" height={230}>
        <PieChart>
          <Pie data={chartData} cx="50%" cy="50%" labelLine={false} label={renderCustomLabel} outerRadius={100} fill="#1890ff" dataKey="value">
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            wrapperStyle={{ zIndex: 1000 }}
            contentStyle={{
              backgroundColor: '#2d3035',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '8px',
              color: '#ffffff',
              padding: '12px 16px',
              fontSize: '14px',
              minWidth: '140px'
            }}
            labelStyle={{ color: '#ffffff' }}
            itemStyle={{ color: '#ffffff' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const item = payload[0];
              const name = item.name ?? item.payload?.name ?? '';
              const value = item.value ?? item.payload?.value ?? 0;
              const total = chartData.reduce((s, d) => s + (d.value || 0), 0);
              const pct = total ? ((Number(value) / total) * 100).toFixed(1) : '0';
              return (
                <div className="defect-distribution-tooltip" style={{ color: '#ffffff', fontSize: '14px', fontWeight: 500, background: 'transparent' }}>
                  <div style={{ color: '#ffffff', fontWeight: 600, marginBottom: '6px', fontSize: '14px' }}>{name}</div>
                  <div style={{ color: '#e8e8e8', fontSize: '13px' }}>{value} ({pct}%)</div>
                </div>
              );
            }}
          />
          <Legend verticalAlign="bottom" height={36} wrapperStyle={{ color: '#fff', paddingTop: '20px' }} />
        </PieChart>
      </ResponsiveContainer>
      <div style={{ marginTop: '20px', padding: '16px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', display: 'flex', justifyContent: 'space-around', gap: '16px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '12px' }}>Total Defects</div>
          <div style={{ color: '#fff', fontSize: '24px', fontWeight: 600 }}>{chartData.reduce((sum, item) => sum + item.value, 0)}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '12px' }}>Most Common</div>
          <div style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>{chartData[0]?.name || 'N/A'}</div>
        </div>
      </div>
    </Card>
  );
};

// Removed: ProductivityComparisonChart — no backend data for planned vs actual

// API may return { value, percentage } for some metrics; ensure we render a primitive
const toDisplayValue = (v) => {
  if (v == null) return 0;
  if (typeof v === 'object' && v !== null && 'value' in v) return v.value;
  return typeof v === 'number' ? v : Number(v) || 0;
};

// Date range helpers for presets
const getRangeByPreset = (preset) => {
  const end = moment().endOf('day');
  let start;
  switch (preset) {
    case 'week': start = moment().subtract(1, 'week').startOf('day'); break;
    case 'month': start = moment().subtract(1, 'month').startOf('day'); break;
    case 'year': start = moment().subtract(1, 'year').startOf('day'); break;
    default: start = moment().subtract(1, 'month').startOf('day');
  }
  return [start, end];
};

const DashboardLog = ({
  shift = 'day',
  dprLogId,
  sowId,
  lightTheme = false,
  // When provided (e.g. global dashboard), show Mainline / Date / Day-Night in filter section
  mainlineOptions,
  mainlineValues,   // array of selected pipeline ids (empty = all)
  mainlineValue,    // legacy single-value (kept for compat)
  onMainlineChange,
  filterDate,
  onFilterDateChange,
  filterShift,
  onFilterShiftChange,
  filterSectionLoading = false,
}) => {
  const [overviewPeriod, setOverviewPeriod] = useState('Monthly');
  const [bothShift, setBothShift] = useState(false);
  // Fullscreen card overlay state – clicking Expand opens the card in a full-viewport overlay
  const [fullscreenCard, setFullscreenCard] = useState(null);
  const toggleFullscreen = (key) => setFullscreenCard(prev => prev === key ? null : key);

  // ESC key closes fullscreen
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setFullscreenCard(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Helper: returns inline style that makes the element cover the full viewport
  const fsStyle = (cardKey, baseStyle = {}) =>
    fullscreenCard === cardKey
      ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, overflow: 'auto', borderRadius: 0, margin: 0, padding: '24px', backgroundColor: lightTheme ? '#fff' : '#1a1d24', boxSizing: 'border-box' }
      : baseStyle;

  // Theme-aware colours for icon buttons
  const btnBg = lightTheme ? '#F3F4F6' : 'rgba(255,255,255,0.1)';
  const btnBgHover = lightTheme ? '#E5E7EB' : 'rgba(255,255,255,0.2)';
  const btnBorder = lightTheme ? '1px solid #D1D5DB' : '1px solid rgba(255,255,255,0.15)';
  const btnIconColor = lightTheme ? '#374151' : 'rgba(255,255,255,0.85)';
  const btnTextColor = lightTheme ? '#374151' : 'rgba(255,255,255,0.85)';

  // Reusable expand button – opens card in full-screen overlay
  const ExpandBtn = ({ cardKey }) => {
    const isFS = fullscreenCard === cardKey;
    return (
      <button
        onClick={() => toggleFullscreen(cardKey)}
        title={isFS ? 'Exit full screen (Esc)' : 'Open full screen'}
        style={{ background: isFS ? (lightTheme ? '#1d4ed8' : '#1890ff') : btnBg, border: isFS ? '1px solid transparent' : btnBorder, borderRadius: '5px', cursor: 'pointer', padding: '4px 6px', display: 'flex', alignItems: 'center', flexShrink: 0, transition: 'all 0.15s' }}
        onMouseEnter={e => { if (!isFS) e.currentTarget.style.background = btnBgHover; }}
        onMouseLeave={e => { if (!isFS) e.currentTarget.style.background = btnBg; }}
      >
        {isFS ? <Minimize2 size={13} color="#fff" /> : <Maximize2 size={13} color={btnIconColor} />}
      </button>
    );
  };

  // Graph ↔ Table toggle state
  const [tableViews, setTableViews] = useState({});
  const toggleView = (key) => setTableViews(prev => ({ ...prev, [key]: !prev[key] }));
  const isTableView = (key) => !!tableViews[key];

  // Fullscreen helpers: in fullscreen always show BOTH chart and table side by side
  const isSectionFS = (sectionKey) => fullscreenCard === `section_${sectionKey}`;
  const isCardFS = (cardKey) => fullscreenCard === cardKey;
  // Whether to show chart: always in FS, or when not in table-only toggle
  const showChartFor = (sectionKey, viewKey) => isSectionFS(sectionKey) || !isTableView(viewKey);
  // Whether to show table: always in FS, or when in table-only toggle
  const showTableFor = (sectionKey, viewKey) => isSectionFS(sectionKey) || isTableView(viewKey);
  // Always full-width cols; in FS both chart+table stack vertically (column layout)
  const fsColSpan = () => 24;

  // Reusable chart/table toggle button — clearly labelled for visibility
  const ViewToggleBtn = ({ cardKey }) => {
    const active = isTableView(cardKey);
    return (
      <button
        onClick={() => toggleView(cardKey)}
        title={active ? 'Switch to chart view' : 'Switch to table view'}
        style={{
          background: active ? (lightTheme ? '#1d4ed8' : '#1890ff') : btnBg,
          border: active ? '1px solid transparent' : btnBorder,
          borderRadius: '5px',
          cursor: 'pointer',
          padding: '4px 9px',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          flexShrink: 0,
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
      >
        {active
          ? <><BarChart2 size={13} color="#fff" /><span style={{ fontSize: '11px', color: '#fff', fontWeight: 600 }}>Chart</span></>
          : <><LayoutList size={13} color={btnIconColor} /><span style={{ fontSize: '11px', color: btnTextColor, fontWeight: 600 }}>Table</span></>
        }
      </button>
    );
  };

  // Helper: simple table for chart cards — theme-aware
  const tblBorder = lightTheme ? '1px solid #E5E7EB' : '1px solid rgba(255,255,255,0.1)';
  const tblHdrBg  = lightTheme ? '#F9FAFB' : 'rgba(255,255,255,0.1)';
  const tblRowAlt = lightTheme ? '#F9FAFB' : 'rgba(255,255,255,0.02)';
  const tblHdrTxt = lightTheme ? '#1f2937' : '#fff';
  const tblCellTxt= lightTheme ? '#374151' : 'rgba(255,255,255,0.85)';
  const tblValTxt = lightTheme ? '#111827' : '#fff';
  const SimpleTable = ({ columns, rows }) => (
    <div style={{ backgroundColor: lightTheme ? '#fff' : 'rgba(255,255,255,0.04)', borderRadius: '8px', border: tblBorder, overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: columns.map(() => '1fr').join(' '), backgroundColor: tblHdrBg, padding: '8px 16px', borderBottom: tblBorder }}>
        {columns.map((col, i) => <div key={i} style={{ color: tblHdrTxt, fontSize: '12px', fontWeight: 600, textAlign: i > 0 ? 'center' : 'left' }}>{col}</div>)}
      </div>
      {rows.map((row, ri) => (
        <div key={ri} style={{ display: 'grid', gridTemplateColumns: columns.map(() => '1fr').join(' '), padding: '8px 16px', borderBottom: ri < rows.length - 1 ? tblBorder : 'none', backgroundColor: ri % 2 === 0 ? tblRowAlt : 'transparent' }}>
          {row.map((cell, ci) => <div key={ci} style={{ color: ci > 0 ? tblValTxt : tblCellTxt, fontSize: '12px', textAlign: ci > 0 ? 'center' : 'left' }}>{cell}</div>)}
        </div>
      ))}
    </div>
  );
  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  // Global filters (wireframe header)
  const [dateRangePreset, setDateRangePreset] = useState('month'); // 'custom' | 'week' | 'month' | 'year'
  const [customDateRange, setCustomDateRange] = useState(() => getRangeByPreset('month'));
  const [shiftFilter, setShiftFilter] = useState('all'); // 'all' | 'day' | 'night'
  const [sectionFilter, setSectionFilter] = useState('welding'); // 'welding' | 'ndt' | 'production'
  const [projectFilter, setProjectFilter] = useState('current'); // 'current' | projectId when multiple projects supported

  // Fetch dashboard metrics (respects global filters: date range, shift, section)
  const fetchDashboardMetrics = useCallback(async () => {
    if (!dprLogId && !sowId) return;

    setIsLoadingMetrics(true);
    try {
      const shiftValue = shiftFilter === 'all' ? 'both' : (shiftFilter === 'day' ? 'day' : 'night');
      const [start, end] = dateRangePreset === 'custom' && customDateRange?.[0] && customDateRange?.[1]
        ? customDateRange
        : getRangeByPreset(dateRangePreset);

      const params = {
        shift: shiftValue,
        fromDate: start ? moment(start).format('YYYY-MM-DD') : undefined,
        toDate: end ? moment(end).format('YYYY-MM-DD') : undefined,
        section: sectionFilter
      };
      if (dprLogId) params.dprLogId = dprLogId;
      if (sowId) params.sowId = sowId;

      const response = await axiosConfig.get(`/dpr-dashboard/metrics`, { params });

      // Handle both { data: { ... } } and flat { ... } response shapes
      const responseData = response?.data?.data ?? response?.data;
      if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
        setDashboardMetrics(responseData);
        console.log('Dashboard metrics fetched:', responseData);
      }
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
    } finally {
      setIsLoadingMetrics(false);
    }
  }, [dprLogId, sowId, shift, shiftFilter, dateRangePreset, customDateRange, sectionFilter]);

  // Fetch metrics when component mounts or when dependencies change
  useEffect(() => {
    fetchDashboardMetrics();
  }, [fetchDashboardMetrics]);

  const dashboardSections = [
    { key: 'productivity', title: 'Productivity (Weld Log)', fullWidth: true },
    { key: 'safety', title: 'Safety', fullWidth: true },
    { key: 'quality', title: 'Quality', fullWidth: true },
    { key: 'personnel', title: 'Personnel', fullWidth: true },
    { key: 'productionImpact', title: 'Production Impact', fullWidth: true },
    { key: 'consumables', title: 'Consumables', fullWidth: true }
  ];

  const effectiveDateRange = dateRangePreset === 'custom' ? customDateRange : getRangeByPreset(dateRangePreset);

  return (
    <div
      className={`dpr-dashboard-log ${lightTheme ? 'light-theme' : ''}`}
      style={{
        padding: lightTheme ? '12px 8px' : '24px',
        backgroundColor: lightTheme ? '#FFFFFF' : '#1a1d24',
        minHeight: '400px',
        borderRadius: lightTheme ? '0' : '8px',
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      <style>
        {`
          .dashboard-accordion .ant-collapse-item {
            background-color: transparent !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
            border-radius: 8px !important;
            margin-bottom: 16px !important;
          }
          .dashboard-accordion .ant-collapse-header {
            background-color: rgba(255, 255, 255, 0.05) !important;
            border-radius: 8px !important;
            padding: 16px 20px !important;
            color: #fff !important;
          }
          .dashboard-accordion .ant-collapse-header:hover {
            background-color: rgba(255, 255, 255, 0.1) !important;
          }
          .dashboard-accordion .ant-collapse-content {
            background-color: transparent !important;
            border: none !important;
          }
          .dashboard-accordion .ant-collapse-content-box {
            padding: 20px !important;
          }
          .dashboard-card {
            border-radius: 8px !important;
            background-color: rgba(255, 255, 255, 0.05) !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
          }
          .dashboard-card .ant-card-body {
            padding: 20px !important;
            background: transparent !important;
          }
          .dashboard-card-equal-height {
            height: 100% !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .dashboard-card-equal-height .ant-card-body {
            flex: 1 !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .dashboard-select .ant-select-selector {
            background-color: rgba(255, 255, 255, 0.1) !important;
            border-color: rgba(255, 255, 255, 0.2) !important;
            color: #fff !important;
            border-radius: 4px !important;
          }
          .dashboard-select .ant-select-selection-item {
            color: #fff !important;
          }
          .dashboard-select .ant-select-arrow {
            color: rgba(255, 255, 255, 0.65) !important;
          }
          .dashboard-select:hover .ant-select-selector {
            border-color: rgba(255, 255, 255, 0.3) !important;
          }
          .dashboard-select.ant-select-focused .ant-select-selector {
            border-color: rgba(255, 77, 79, 0.6) !important;
            box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
          }
          /* Light theme overrides */
          .light-theme h2[style*="color: rgb(255, 255, 255)"],
          .light-theme h2[style*="color:#fff"],
          .light-theme h4[style*="color: rgb(255, 255, 255)"],
          .light-theme h4[style*="color:#fff"],
          .light-theme span[style*="color: rgb(255, 255, 255)"],
          .light-theme span[style*="color:#fff"],
          .light-theme div[style*="color: rgb(255, 255, 255)"],
          .light-theme div[style*="color:#fff"],
          .light-theme label[style*="color: rgb(255, 255, 255)"],
          .light-theme label[style*="color:#fff"] {
            color: #000 !important;
          }
          .light-theme .dashboard-card {
            background-color: #FFFFFF !important;
            border-color: #E5E7EB !important;
          }
          /* Override all rgba white colors in light theme */
          .light-theme div[style*="color: rgba(255, 255, 255"],
          .light-theme span[style*="color: rgba(255, 255, 255"] {
            color: rgba(0, 0, 0, 0.75) !important;
          }
          /* Input and textarea text and placeholder in light theme */
          .light-theme input[style*="color: rgb(255, 255, 255)"],
          .light-theme input[style*="color:#fff"],
          .light-theme textarea[style*="color: rgb(255, 255, 255)"],
          .light-theme textarea[style*="color:#fff"],
          .light-theme .ant-input[style*="color: rgb(255, 255, 255)"],
          .light-theme .ant-input[style*="color:#fff"] {
            color: rgba(0, 0, 0, 0.85) !important;
          }
          .light-theme input::placeholder,
          .light-theme textarea::placeholder,
          .light-theme .ant-input::placeholder {
            color: rgba(0, 0, 0, 0.45) !important;
          }
          .light-theme .ant-input-number-input {
            color: rgba(0, 0, 0, 0.85) !important;
          }
          /* Select component text in light theme */
          .light-theme .ant-select-selection-item {
            color: rgba(0, 0, 0, 0.85) !important;
          }
          .light-theme .ant-select-selection-placeholder {
            color: rgba(0, 0, 0, 0.45) !important;
          }
          .light-theme .dashboard-select .ant-select-selection-item {
            color: rgba(0, 0, 0, 0.85) !important;
          }
          .light-theme .dashboard-select .ant-select-selector {
            background-color: #FFFFFF !important;
            border-color: #D1D5DB !important;
            color: #374151 !important;
          }
          .light-theme .dashboard-select:hover .ant-select-selector {
            border-color: #9CA3AF !important;
          }
          .light-theme .dashboard-select.ant-select-focused .ant-select-selector {
            background-color: #FFFFFF !important;
            border-color: #00C9A7 !important;
            box-shadow: 0 0 0 2px rgba(0, 201, 167, 0.15) !important;
          }
          .dpr-dashboard-log .recharts-text {
            fill: rgba(255, 255, 255, 0.92) !important;
          }
          .dpr-dashboard-log .recharts-legend-item-text {
            color: rgba(255, 255, 255, 0.92) !important;
          }
          .dpr-dashboard-log .recharts-cartesian-axis-tick-value {
            fill: rgba(255, 255, 255, 0.9) !important;
          }
          /* Light theme: chart and axis text visible on white */
          .dpr-dashboard-log.light-theme .recharts-text,
          .dpr-dashboard-log.light-theme .recharts-cartesian-axis-tick-value {
            fill: #374151 !important;
          }
          .dpr-dashboard-log.light-theme .recharts-legend-item-text {
            color: #374151 !important;
          }
          .dpr-dashboard-log.light-theme .recharts-default-tooltip {
            background: #FFFFFF !important;
            color: #1f2937 !important;
            border: 1px solid #E5E7EB !important;
          }
          /* Light theme: dropdowns white bg, black text */
          .dpr-light-dropdown.ant-select-dropdown {
            background: #FFFFFF !important;
          }
          .dpr-light-dropdown .ant-select-item-option {
            color: #1f2937 !important;
          }
          .dpr-light-dropdown .ant-select-item-option-selected {
            background: #f3f4f6 !important;
            color: #1f2937 !important;
          }
          .dpr-light-dropdown .ant-select-item-option-active {
            background: #f9fafb !important;
          }
          .dpr-light-datepicker-dropdown.ant-picker-dropdown {
            background: #fff !important;
          }
          .dpr-light-datepicker-dropdown .ant-picker-content th,
          .dpr-light-datepicker-dropdown .ant-picker-content td,
          .dpr-light-datepicker-dropdown .ant-picker-header button {
            color: #1f2937 !important;
          }
          .dpr-light-datepicker-dropdown .ant-picker-cell-in-view .ant-picker-cell-inner {
            color: #1f2937 !important;
          }
          .dpr-dashboard-log.light-theme .ant-radio-button-wrapper {
            color: #374151 !important;
            border-color: #d1d5db !important;
          }
          .dpr-dashboard-log.light-theme .ant-radio-button-wrapper-checked {
            background: #00C9A7 !important;
            border-color: #00C9A7 !important;
            color: #fff !important;
          }
          .dpr-dashboard-log.light-theme .dashboard-accordion .ant-collapse-header {
            color: #1f2937 !important;
          }
          .dpr-dashboard-log.light-theme .dashboard-accordion .ant-collapse-item {
            border-color: rgba(0, 0, 0, 0.12) !important;
          }
          .dpr-dashboard-log.light-theme .dashboard-accordion .ant-collapse-header {
            background-color: rgba(0, 0, 0, 0.04) !important;
          }
          .dpr-dashboard-log.light-theme .dashboard-accordion .ant-collapse-header:hover {
            background-color: rgba(0, 0, 0, 0.08) !important;
          }
          .dpr-dashboard-log.light-theme .ant-typography,
          .dpr-dashboard-log.light-theme h1, .dpr-dashboard-log.light-theme h2, .dpr-dashboard-log.light-theme h4,
          .dpr-dashboard-log.light-theme .dashboard-card .ant-typography {
            color: #1f2937 !important;
          }
          .dpr-dashboard-log.light-theme div[style*="color: rgba(255,255,255"],
          .dpr-dashboard-log.light-theme span[style*="color: rgba(255,255,255"],
          .dpr-dashboard-log.light-theme div[style*="color: rgb(255, 255, 255)"],
          .dpr-dashboard-log.light-theme span[style*="color: rgb(255, 255, 255)"] {
            color: #374151 !important;
          }
          .dpr-dashboard-log .recharts-tooltip-wrapper,
          .dpr-dashboard-log .recharts-default-tooltip {
            color: #ffffff !important;
            background: #2d3035 !important;
          }
          .dpr-dashboard-log .defect-distribution-tooltip,
          .dpr-dashboard-log .defect-distribution-tooltip * {
            color: #ffffff !important;
          }
          .dpr-dashboard-log .consumables-trend-tooltip,
          .dpr-dashboard-log .consumables-trend-tooltip * {
            color: #ffffff !important;
          }
          .dpr-dashboard-log .ant-picker,
          .dpr-dashboard-log .ant-select-selector,
          .dpr-dashboard-log .ant-radio-button-wrapper {
            background: rgba(255,255,255,0.08) !important;
            border-color: rgba(255,255,255,0.2) !important;
            color: #fff !important;
          }
          .dpr-dashboard-log .ant-picker-input input { color: #fff !important; }
          .dpr-dashboard-log .ant-select-selection-item { color: #fff !important; }
          .dpr-dashboard-log .ant-radio-button-wrapper-checked { background: #ff4d4f !important; border-color: #ff4d4f !important; }
          /* Light theme overrides for form controls */
          .dpr-dashboard-log.light-theme .ant-select-selector {
            background: #FFFFFF !important;
            border-color: #D1D5DB !important;
            color: #374151 !important;
          }
          .dpr-dashboard-log.light-theme .ant-select-selection-item { color: #374151 !important; }
          .dpr-dashboard-log.light-theme .ant-select-selection-placeholder { color: #9CA3AF !important; }
          .dpr-dashboard-log.light-theme .ant-select-arrow { color: #6B7280 !important; }
          .dpr-dashboard-log.light-theme .ant-picker {
            background: #FFFFFF !important;
            border-color: #D1D5DB !important;
            box-shadow: 0 1px 2px 0 rgba(0,0,0,0.05) !important;
          }
          .dpr-dashboard-log.light-theme .ant-picker-input input { color: #374151 !important; }
          .dpr-dashboard-log.light-theme .ant-picker-suffix { color: #6B7280 !important; }
          .dpr-dashboard-log.light-theme .ant-radio-button-wrapper {
            background: #FFFFFF !important;
            border-color: #D1D5DB !important;
            color: #374151 !important;
          }
          .dpr-dashboard-log.light-theme .ant-radio-button-wrapper-checked {
            background: #00C9A7 !important;
            border-color: #00C9A7 !important;
            color: #fff !important;
          }
          .dpr-dashboard-log .ant-table { background: transparent !important; color: #fff !important; }
          .dpr-dashboard-log .ant-table-thead > tr > th { background: rgba(255,255,255,0.1) !important; color: rgba(255,255,255,0.9) !important; border-color: rgba(255,255,255,0.15) !important; }
          .dpr-dashboard-log .ant-table-tbody > tr > td { border-color: rgba(255,255,255,0.1) !important; }
          .dpr-dashboard-log .ant-table-tbody > tr:hover > td { background: rgba(255,255,255,0.05) !important; }
          .dpr-dashboard-log .ant-table-placeholder { color: rgba(255,255,255,0.5) !important; }
        `}
      </style>

      {/* 🧭 GLOBAL CONTROLS – single row filter bar */}
      <Card className="dashboard-card" style={{ marginBottom: '20px' }}>

        {/* Header: filter icon label + export buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
          <SlidersHorizontal size={14} color={lightTheme ? '#6B7280' : 'rgba(255,255,255,0.6)'} />
          <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: lightTheme ? '#6B7280' : 'rgba(255,255,255,0.6)' }}>Filters</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <CrcButton label="Export PDF" variant="tertiary" size="small" icon={FileText} onClick={() => {}} />
          </div>
        </div>

        {/* All filter controls in one row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '20px' }}>

          {/* Mainline – CustomMultiSelect */}
          {mainlineOptions && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: lightTheme ? '#374151' : 'rgba(255,255,255,0.85)', fontSize: '13px', whiteSpace: 'nowrap' }}>Mainline:</span>
              <CustomMultiSelect
                values={Array.isArray(mainlineValues) ? mainlineValues : (mainlineValue ? [mainlineValue] : [])}
                options={mainlineOptions || []}
                onChange={(ids) => { if (onMainlineChange) onMainlineChange(ids); }}
                placeholder="All Mainlines"
              />
            </div>
          )}

          {/* Date */}
          {mainlineOptions && filterDate != null && onFilterDateChange && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: lightTheme ? '#374151' : 'rgba(255,255,255,0.85)', fontSize: '13px', whiteSpace: 'nowrap' }}>Date:</span>
              <DatePicker
                value={filterDate}
                onChange={onFilterDateChange}
                format="DD-MMM-YYYY"
                allowClear={false}
                size="small"
                style={lightTheme ? { width: 140, height: 36, borderRadius: 6, border: '1px solid #E5E7EB', backgroundColor: '#FFFFFF', color: '#374151' } : { width: 140 }}
                className={lightTheme ? 'dpr-date-picker' : ''}
                dropdownClassName={lightTheme ? 'dpr-date-picker-dropdown' : undefined}
                inputStyle={lightTheme ? { color: '#374151', fontWeight: 500 } : undefined}
              />
            </div>
          )}

          {/* Divider */}
          {mainlineOptions && (
            <div style={{ width: 1, height: 28, backgroundColor: lightTheme ? '#E5E7EB' : 'rgba(255,255,255,0.15)', flexShrink: 0 }} />
          )}

          {/* Date range – Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: lightTheme ? '#374151' : 'rgba(255,255,255,0.85)', fontSize: '13px', whiteSpace: 'nowrap' }}>Date range:</span>
            <Toggle
              options={[
                { id: 'custom', label: 'Custom' },
                { id: 'week', label: 'Week' },
                { id: 'month', label: 'Month' },
                { id: 'year', label: 'Year' },
              ]}
              activeId={dateRangePreset}
              onToggle={setDateRangePreset}
            />
          </div>

          {/* Shift – CustomSingleSelect */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: lightTheme ? '#374151' : 'rgba(255,255,255,0.85)', fontSize: '13px', whiteSpace: 'nowrap' }}>Shift:</span>
            <div style={{ width: 90 }}>
              <CustomSingleSelect
                value={shiftFilter === 'all' ? 'All' : shiftFilter === 'day' ? 'Day' : 'Night'}
                options={['All', 'Day', 'Night']}
                onChange={(val) => setShiftFilter(val.toLowerCase())}
                placeholder="Shift"
              />
            </div>
          </div>

        </div>

        {/* Custom range picker – shows below when Custom is selected */}
        {dateRangePreset === 'custom' && (
          <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <RangePicker
              value={customDateRange && customDateRange[0] && customDateRange[1] ? [moment(customDateRange[0]), moment(customDateRange[1])] : null}
              onChange={(dates) => setCustomDateRange(dates && dates[0] && dates[1] ? [dates[0].toDate(), dates[1].toDate()] : getRangeByPreset('month'))}
              format="DD-MMM-YYYY"
              size="small"
              allowClear={false}
              style={lightTheme ? { height: 36, borderRadius: 6, border: '1px solid #D1D5DB', backgroundColor: '#FFFFFF', color: '#374151' } : {}}
              className={lightTheme ? 'dpr-date-picker' : ''}
              dropdownClassName={lightTheme ? 'dpr-date-picker-dropdown' : undefined}
              inputStyle={lightTheme ? { color: '#374151', fontWeight: 500 } : undefined}
            />
            <span style={{ color: lightTheme ? '#9CA3AF' : 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
              {effectiveDateRange && effectiveDateRange[0] && effectiveDateRange[1]
                ? `${moment(effectiveDateRange[0]).format('DD MMM YYYY')} – ${moment(effectiveDateRange[1]).format('DD MMM YYYY')}`
                : ''}
            </span>
          </div>
        )}
      </Card>

      {/* When mainline filter is loading show a spinner overlay */}
      {mainlineOptions && filterSectionLoading && (
        <div style={{ padding: '24px', textAlign: 'center', color: lightTheme ? '#64748B' : 'rgba(255,255,255,0.7)', fontSize: '15px' }}>
          Loading DPR log...
        </div>
      )}

      {/* Chart content – always shown */}
      {(!mainlineOptions || !filterSectionLoading) && (
        <>

      {/* Loading State */}
      {isLoadingMetrics ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48, color: '#ff4d4f' }} spin />}
            size="large"
          />
          <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '16px', fontWeight: 400 }}>
            Loading Dashboard...
          </div>
        </div>
      ) : (
        <>
            {/* 🔹 SECTION 1: PROJECT HEALTH KPIs */}
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}> Project health KPIs</div>
            {(() => {
              const hasMetrics = dashboardMetrics != null;
              const p = dashboardMetrics?.productivity ?? dashboardMetrics?.weldLog ?? dashboardMetrics?.summary ?? dashboardMetrics ?? {};

              // Raw values from API (null/undefined when not yet loaded)
              const rawWelds = p?.totalWelds ?? p?.weldsLogged ?? p?.weldCount ?? dashboardMetrics?.totalWelds;
              const rawRate  = p?.acceptanceRate ?? p?.rtAcceptanceRate ?? dashboardMetrics?.acceptanceRate;
              const rawRt    = p?.rtCompleted ?? p?.rtAccepted ?? p?.rtCount ?? dashboardMetrics?.rtCompleted;
              const rawCuts  = p?.cutoffs ?? p?.cutOuts ?? p?.cutout ?? dashboardMetrics?.cutOuts;
              const rawDprs  = dashboardMetrics?.dprCount ?? dashboardMetrics?.totalDprs ?? dashboardMetrics?.summary?.dprCount;

              // Use real values when available, otherwise fall back to sample data
              const totalWelds    = hasMetrics ? toDisplayValue(rawWelds)          : 85;
                  const acceptanceRate = hasMetrics ? (Number(rawRate) || 10) : 92.9;
              const rtCompleted   = hasMetrics ? toDisplayValue(rawRt)             : 78;
              const cutOuts       = hasMetrics ? toDisplayValue(rawCuts)           : 3;
              const dprCount      = hasMetrics ? (rawDprs != null ? toDisplayValue(rawDprs) : 0) : 12;

              const rateColor = acceptanceRate >= 95 ? '#52c41a' : acceptanceRate >= 85 ? '#faad14' : '#ff4d4f';
              return (
                <Row gutter={[12, 12]} justify="center" style={{ marginBottom: '24px' }}>
                  <Col xs={12} sm={8} lg={4} xl={4}>
                    <Card size="small" className="dashboard-card" style={{ textAlign: 'center' }}>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>Welds Logged</div>
                      <div style={{ color: '#fff', fontSize: '28px', fontWeight: 700 }}>{totalWelds}</div>
                      <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px' }}>Joints welded at Station</div>
                    </Card>
                  </Col>
                  <Col xs={12} sm={8} lg={4} xl={4}>
                    <Card size="small" className="dashboard-card" style={{ textAlign: 'center' }}>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>Acceptance %</div>
                      <div style={{ color: rateColor, fontSize: '28px', fontWeight: 700 }}>{`${acceptanceRate.toFixed(1)}%`}</div>
                      <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px' }}>RT acceptance rate</div>
                    </Card>
                  </Col>
                  <Col xs={12} sm={8} lg={4} xl={4}>
                    <Card size="small" className="dashboard-card" style={{ textAlign: 'center' }}>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>RT Completed</div>
                      <div style={{ color: '#1890ff', fontSize: '28px', fontWeight: 700 }}>{rtCompleted}</div>
                      <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px' }}>QA throughput</div>
                    </Card>
                  </Col>
                  <Col xs={12} sm={8} lg={4} xl={4}>
                    <Card size="small" className="dashboard-card" style={{ textAlign: 'center' }}>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>Cut Outs</div>
                      <div style={{ color: cutOuts > 0 ? '#ff4d4f' : '#fff', fontSize: '28px', fontWeight: 700 }}>{cutOuts}</div>
                      <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px' }}>Joints removed</div>
                    </Card>
                  </Col>
                  <Col xs={12} sm={8} lg={4} xl={4}>
                    <Card size="small" className="dashboard-card" style={{ textAlign: 'center' }}>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>Total DPRs</div>
                      <div style={{ color: '#fff', fontSize: '28px', fontWeight: 700 }}>{dprCount || 15}</div>
                      <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px' }}>Reports submitted</div>
                    </Card>
                  </Col>
                </Row>
              );
            })()}

            {/* 🔹 SECTION 2: PRODUCTIVITY OVERVIEW (Weld Log summary) */}
                {/* <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>Productivity overview</div> */}
                {/* <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={24} lg={24}>
                <Card className="dashboard-card" style={fsStyle('weldLog', { marginBottom: 0, height: 420, overflow: 'auto' })}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <Title level={5} style={{ margin: 0, color: '#fff', fontSize: '14px' }}>Weld Log summary</Title>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <ViewToggleBtn cardKey="weldLog" />
                      <ExpandBtn cardKey="weldLog" />
                    </div>
                  </div>
                  {(() => {
                    const p = dashboardMetrics?.productivity;
                    const summaryData = [
                      { metric: 'Welded (Stn 1)', value: toDisplayValue(p?.totalWelds) || 85, fill: '#1890ff' },
                      { metric: 'RT Accepted', value: toDisplayValue(p?.rtAccepted) || 72, fill: '#52c41a' },
                      { metric: 'RT Rejected', value: toDisplayValue(p?.rtRejected) || 8, fill: '#ff4d4f' },
                      { metric: 'Repairs', value: toDisplayValue(p?.repairs) || 5, fill: '#faad14' },
                      { metric: 'Cut Outs', value: toDisplayValue(p?.cutoffs) || 2, fill: '#ff4d4f' },
                      { metric: 'Blow Through', value: toDisplayValue(p?.blowThrough) || 3, fill: '#722ed1' }
                    ];
                    const wlIsFS = isCardFS('weldLog');
                    const wlChart = (
                      <ResponsiveContainer width="100%" height={wlIsFS ? 420 : 260}>
                        <BarChart data={summaryData} layout="vertical" margin={{ left: 90, right: 20, top: 5, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                          <XAxis type="number" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} />
                          <YAxis type="category" dataKey="metric" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} width={90} />
                          <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
                          <Bar dataKey="value" name="Count" radius={[0, 4, 4, 0]}>
                            {summaryData.map((entry, idx) => <Cell key={`wl-${idx}`} fill={entry.fill} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    );
                    const wlTable = <SimpleTable columns={['Metric', 'Count']} rows={summaryData.map(r => [r.metric, r.value])} />;
                    if (wlIsFS) return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        <div>{wlChart}</div>
                        <div>{wlTable}</div>
                      </div>
                    );
                    return isTableView('weldLog') ? wlTable : wlChart;
                  })()}

                  <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px', marginTop: '8px' }}>From: Weld Log (Joint No, Welder ID, Status, RT, Repair, Cut Out, Blow Through)</div>
                </Card>
              </Col>
            </Row> */}

            <div style={{ height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.1)', margin: '32px 0' }} />

            {/* Dashboard Grid (existing section cards) */}
          <Row gutter={[16, 16]}>
            {dashboardSections.map((item) => (
              <Col xs={24} lg={12} key={item.key} style={{ display: 'flex' }}>
                <Card className="dashboard-card" style={fsStyle(`section_${item.key}`, { width: '100%', height: 420, overflow: 'auto', display: 'flex', flexDirection: 'column' })}>
              {item.title === 'Safety' ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: 'rgba(82, 196, 26, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid rgba(82, 196, 26, 0.5)' }}>
                      <SafetyOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>SAFETY</Title>
                    <div style={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '11px', marginTop: '2px' }}>Data from: Safety Entry</div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                      <ViewToggleBtn cardKey="section_safety_view" />
                      <ExpandBtn cardKey={`section_${item.key}`} />
                    </div>
                  </div>
                  <Row gutter={[24, 16]}>
                    {showChartFor(item.key, 'section_safety_view') && <Col xs={24} lg={fsColSpan(item.key)}>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Incidents by category</div>
                      {(() => {
                        const sampleSafety = [
                          { name: 'Fatal Accidents', value: 0 }, { name: 'LTIs', value: 0 }, { name: 'RWC', value: 1 },
                          { name: 'MTC', value: 4 }, { name: 'Near Misses', value: 12 }, { name: 'FAC', value: 6 },
                          { name: 'Environmental', value: 2 }, { name: 'Other Incidents', value: 3 }, { name: 'Observation Cards', value: 18 }
                        ];
                        const apiList = dashboardMetrics?.safetyMetrics?.entriesList;
                        const hasApiData = Array.isArray(apiList) && apiList.length > 0;
                        const chartData = hasApiData
                          ? apiList.map(e => ({ name: e.name || e.keyName || '—', value: toDisplayValue(e.todayValue ?? e.value) }))
                          : sampleSafety;
                        const dataToShow = chartData.length > 0 ? chartData : sampleSafety;
                        return (
                          <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={dataToShow} layout="vertical" margin={{ left: 130, right: 20, top: 5, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                              <XAxis type="number" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} />
                              <YAxis type="category" dataKey="name" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} width={125} />
                              <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
                              <Bar dataKey="value" fill="#52c41a" name="Count" radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        );
                      })()}
                    </Col>}
                    {showTableFor(item.key, 'section_safety_view') && <Col xs={24} lg={fsColSpan(item.key)}>
                      {(() => {
                        const sampleSafetyRows = [
                          { name: 'Fatal Accidents', todayValue: 0 }, { name: 'LTIs', todayValue: 0 },
                          { name: 'RWC', todayValue: 1 }, { name: 'MTC', todayValue: 4 },
                          { name: 'Near Misses', todayValue: 12 }, { name: 'FAC', todayValue: 6 },
                          { name: 'Environmental', todayValue: 2 }, { name: 'Other Incidents', todayValue: 3 },
                          { name: 'Observation Cards', todayValue: 18 }
                        ];
                        const apiList = dashboardMetrics?.safetyMetrics?.entriesList;
                        const hasApiData = Array.isArray(apiList) && apiList.length > 0;
                        const tableRows = hasApiData ? apiList : sampleSafetyRows;
                        const totalVal = hasApiData ? toDisplayValue(dashboardMetrics?.safetyMetrics?.total) : 46;
                        return (
                          <>
                            <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', backgroundColor: 'rgba(255,255,255,0.1)', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600 }}>Metric</div>
                                <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600, textAlign: 'center' }}>Value</div>
                              </div>
                              {tableRows.map((entry, index, arr) => (
                                <div key={entry.id || entry.keyName || entry.name || index} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', padding: '8px 16px', borderBottom: index < arr.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none', backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>{entry.name || entry.keyName}</div>
                                  <div style={{ color: '#fff', fontSize: '13px', textAlign: 'center' }}>{toDisplayValue(entry.todayValue ?? entry.value ?? 0)}</div>
                                </div>
                              ))}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '16px', padding: '12px', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                              <div style={{ textAlign: 'center' }}><div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px' }}>Total Incidents</div><div style={{ color: '#fff', fontSize: '18px', fontWeight: 700 }}>{totalVal}</div></div>
                            </div>
                          </>
                        );
                      })()}
                    </Col>}
                  </Row>
                </div>
                  ) : item.title === 'Quality' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      {/* Quality Header */}
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(24, 144, 255, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid rgba(24, 144, 255, 0.5)', flexShrink: 0 }}>
                            <CheckCircleOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                          </div>
                          <div>
                            <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>QUALITY</Title>
                            <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px', marginTop: '2px' }}>Record daily weld status and defects.</div>
                          </div>
                          <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', flexShrink: 0 }}>
                            <ViewToggleBtn cardKey="section_quality_view" />
                            <ExpandBtn cardKey={`section_${item.key}`} />
                          </div>
                        </div>
                      </div>

                      {/* Defect Tracking label */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <WarningOutlined style={{ color: '#faad14', fontSize: '14px' }} />
                        <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Defect Tracking</span>
                      </div>

                      <Row gutter={[24, 16]}>
                          {/* Chart view: single bar per defect — incident count only */}
                        {showChartFor(item.key, 'section_quality_view') && <Col xs={24} lg={fsColSpan(item.key)}>
                          {(() => {
                            const qEntries = dashboardMetrics?.qualityReport?.entriesList;
                            const sampleQuality = [
                              { name: 'High Low Reject', value: 166 },
                              { name: 'Station Reject', value: 57 },
                              { name: 'RT Repair', value: 34 },
                              { name: 'RT Cut Off', value: 27 }
                            ];
                            const chartData = qEntries?.length > 0
                              ? qEntries.map(e => ({
                                name: e.name || e.key || e.keyName || '',
                                value: toDisplayValue(e.value ?? e.today ?? e.todayValue ?? e.total ?? e.totalValue ?? e.month ?? e.monthValue ?? 0)
                              }))
                              : sampleQuality;
                            return (
                              <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 5, bottom: 45 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                  <XAxis dataKey="name" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} angle={-20} textAnchor="end" height={55} />
                                  <YAxis stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} allowDecimals={false} />
                                  <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
                                  <Bar dataKey="value" fill="#1890ff" name="Incidents" radius={[4, 4, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            );
                          })()}
                        </Col>}

                          {/* Table view: Defect Type | Value (incident count only) */}
                        {showTableFor(item.key, 'section_quality_view') && <Col xs={24} lg={fsColSpan(item.key)}>
                          {(() => {
                            const qEntries = dashboardMetrics?.qualityReport?.entriesList;
                            const sampleQuality = [
                              { name: 'High Low Reject', value: 166 },
                              { name: 'Station Reject', value: 57 },
                              { name: 'RT Repair', value: 34 },
                              { name: 'RT Cut Off', value: 27 }
                            ];
                            const tableData = qEntries?.length > 0
                              ? qEntries.map(e => ({
                                name: e.name || e.key || e.keyName || '',
                                value: toDisplayValue(e.value ?? e.today ?? e.todayValue ?? e.total ?? e.totalValue ?? e.month ?? e.monthValue ?? 0)
                              }))
                              : sampleQuality;
                              const totalIncidents = tableData.reduce((s, r) => s + r.value, 0);
                            return (
                              <>
                                <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', marginBottom: '14px' }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', backgroundColor: 'rgba(255,255,255,0.1)', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                    <div style={{ color: '#fff', fontSize: '12px', fontWeight: 600 }}>Defect Type</div>
                                    <div style={{ color: '#fff', fontSize: '12px', fontWeight: 600, textAlign: 'center' }}>Incidents</div>
                                  </div>
                                  {tableData.map((row, index) => (
                                    <div key={row.name || index} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', padding: '10px 16px', borderBottom: index < tableData.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none', backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                                      <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>{row.name}</div>
                                      <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600, textAlign: 'center' }}>{row.value}</div>
                                    </div>
                                  ))}
                                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', padding: '10px 16px', backgroundColor: 'rgba(255,255,255,0.07)', borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                                    <div style={{ color: '#fff', fontSize: '12px', fontWeight: 700 }}>TOTAL</div>
                                    <div style={{ color: '#1890ff', fontSize: '13px', fontWeight: 700, textAlign: 'center' }}>{totalIncidents}</div>
                                  </div>
                                </div>
                                <div style={{ backgroundColor: 'rgba(24,144,255,0.12)', borderRadius: '8px', border: '1px solid rgba(24,144,255,0.3)', padding: '12px 16px', textAlign: 'center' }}>
                                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase' }}>Total Incidents</div>
                                  <div style={{ color: '#1890ff', fontSize: '22px', fontWeight: 700 }}>{totalIncidents}</div>
                                </div>
                              </>
                            );
                          })()}
                        </Col>}
                      </Row>
                    </div>
              ) : item.title === 'Productivity (Weld Log)' ? (
                <div>
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: 'rgba(139, 69, 19, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <BarChartOutlined style={{ color: '#8B4513', fontSize: '20px' }} />
                      </div>
                      <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600, whiteSpace: 'nowrap' }}>PRODUCTIVITY</Title>
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', flexShrink: 0 }}>
                        <ViewToggleBtn cardKey="section_productivity_view" />
                        <ExpandBtn cardKey={`section_${item.key}`} />
                      </div>
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px', marginTop: '6px', paddingLeft: '52px' }}>Data from: Weld Log – Joint No, Welder ID, Weld Status, RT, Blow Through, Repair</div>
                  </div>
                  <Row gutter={[24, 16]}>
                    {showChartFor(item.key, 'section_productivity_view') && <Col xs={24} lg={fsColSpan(item.key)}>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Weld Log breakdown</div>
                      {(() => {
                        const p = dashboardMetrics?.productivity;
                        const chartData = [
                          { metric: 'Welded (Stn 1)', value: toDisplayValue(p?.totalWelds) || 85, fill: '#1890ff' },
                          { metric: 'RT Accepted', value: toDisplayValue(p?.rtAccepted) || 72, fill: '#52c41a' },
                          { metric: 'RT Rejected', value: toDisplayValue(p?.rtRejected) || 8, fill: '#ff4d4f' },
                          { metric: 'Repairs', value: toDisplayValue(p?.repairs) || 5, fill: '#faad14' },
                          { metric: 'Cut Outs', value: toDisplayValue(p?.cutoffs) || 2, fill: '#ff4d4f' },
                          { metric: 'Blow Through', value: toDisplayValue(p?.blowThrough) || 3, fill: '#722ed1' }
                        ];
                        return (
                          <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={chartData} layout="vertical" margin={{ left: 100, right: 20, top: 5, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                              <XAxis type="number" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} />
                              <YAxis type="category" dataKey="metric" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '12px' }} width={95} />
                              <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} />
                              <Bar dataKey="value" name="Count" radius={[0, 4, 4, 0]}>
                                {chartData.map((entry, idx) => <Cell key={`pr-${idx}`} fill={entry.fill} />)}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        );
                      })()}
                    </Col>}
                    {showTableFor(item.key, 'section_productivity_view') && <Col xs={24} lg={fsColSpan(item.key)}>
                      {(() => {
                        const p = dashboardMetrics?.productivity;
                        const rows = [
                          { label: 'Welded at Station', value: toDisplayValue(p?.totalWelds) || 85, color: '#fff' },
                          { label: 'RT Completed', value: toDisplayValue(p?.rtCompleted) || 80, color: '#fff' },
                          { label: 'RT Accepted', value: toDisplayValue(p?.rtAccepted) || 72, color: '#52c41a' },
                          { label: 'RT Rejected', value: toDisplayValue(p?.rtRejected) || 8, color: '#ff4d4f' },
                          { label: 'Acceptance Rate', value: `${(Number(p?.acceptanceRate) || 90).toFixed(1)}%`, color: Number(p?.acceptanceRate) >= 95 ? '#52c41a' : '#ff4d4f' },
                          { label: 'Repairs', value: toDisplayValue(p?.repairs) || 5, color: '#faad14' },
                          { label: 'Cut Outs', value: toDisplayValue(p?.cutoffs) || 2, color: '#ff4d4f' },
                          { label: 'Blow Through', value: toDisplayValue(p?.blowThrough) || 3, color: '#722ed1' },
                        ];
                        return (
                          <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', backgroundColor: 'rgba(255,255,255,0.1)', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                              <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600 }}>Metric</div>
                              <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600, textAlign: 'right' }}>Value</div>
                            </div>
                            {rows.map((r, i) => (
                              <div key={r.label} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', padding: '8px 16px', borderBottom: i < rows.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none', backgroundColor: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                                <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>{r.label}</div>
                                <div style={{ color: r.color, fontSize: '13px', fontWeight: 500, textAlign: 'right' }}>{r.value}</div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </Col>}
                  </Row>
                </div>
              ) : item.title === 'Personnel' ? (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  {/* Personnel Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(114, 46, 209, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <TeamOutlined style={{ color: '#722ed1', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>PERSONNEL</Title>
                    <div style={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '11px', marginTop: '2px' }}>Data from: Personnel Entry</div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                      <ViewToggleBtn cardKey="section_personnel_view" />
                      <ExpandBtn cardKey={`section_${item.key}`} />
                    </div>
                  </div>

                  <Row gutter={[16, 16]}>
                    {/* Left: Grouped bar chart (Planned vs Actual by role) */}
                    {showChartFor(item.key, 'section_personnel_view') && <Col xs={24} lg={fsColSpan(item.key)}>
                      {(() => {
                        const personnelSampleData = [
                          { name: 'Welders', planned: 12, actual: 10 },
                          { name: 'Fitters', planned: 8, actual: 7 },
                          { name: 'Helpers', planned: 6, actual: 6 },
                          { name: 'QC Inspector', planned: 3, actual: 2 },
                          { name: 'Supervisors', planned: 2, actual: 2 }
                        ];
                        const chartData = dashboardMetrics?.personnel?.entriesList?.length > 0
                          ? dashboardMetrics.personnel.entriesList.map(e => ({
                            name: e.name || e.keyName,
                            planned: bothShift ? (toDisplayValue(e.plannedDayShift) + toDisplayValue(e.plannedNightShift)) : (shift === 'day' ? toDisplayValue(e.plannedDayShift) : toDisplayValue(e.plannedNightShift)),
                            actual: bothShift ? (toDisplayValue(e.actualDayShift) + toDisplayValue(e.actualNightShift)) : (shift === 'day' ? toDisplayValue(e.actualDayShift) : toDisplayValue(e.actualNightShift))
                          }))
                          : personnelSampleData;
                        return (
                          <div style={{ marginBottom: '20px' }}>
                            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Planned vs Actual by role (hrs)</div>
                            <ResponsiveContainer width="100%" height={260}>
                              <BarChart data={chartData} margin={{ top: 5, right: 5, left: 10, bottom: 40 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                <XAxis dataKey="name" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} angle={-30} textAnchor="end" height={60} />
                                <YAxis stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} tickFormatter={(v) => `${v} hrs`} width={55} />
                                <Tooltip
                                  contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }}
                                  formatter={(value, name) => [`${value} hrs`, name]}
                                />
                                <Legend wrapperStyle={{ color: '#fff' }} />
                                <Bar dataKey="planned" fill="#722ed1" name="Planned" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="actual" fill="#52c41a" name="Actual" radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        );
                      })()}
                    </Col>}

                    {/* Right: Table + summary totals */}
                    {showTableFor(item.key, 'section_personnel_view') && <Col xs={24} lg={fsColSpan(item.key)}>
                      {(() => {
                        const personnelSampleData = [
                          { name: 'Welders', planned: 12, actual: 10 },
                          { name: 'Fitters', planned: 8, actual: 7 },
                          { name: 'Helpers', planned: 6, actual: 6 },
                          { name: 'QC Inspector', planned: 3, actual: 2 },
                          { name: 'Supervisors', planned: 2, actual: 2 }
                        ];
                        const tableData = dashboardMetrics?.personnel?.entriesList?.length > 0
                          ? dashboardMetrics.personnel.entriesList
                          : personnelSampleData.map((item, i) => ({
                            id: i,
                            keyName: item.name,
                            name: item.name,
                            plannedDayShift: item.planned,
                            plannedNightShift: 0,
                            actualDayShift: item.actual,
                            actualNightShift: 0
                          }));
                        const plannedTotal = dashboardMetrics?.personnel?.entriesList?.length > 0
                          ? toDisplayValue(dashboardMetrics?.personnel?.planned)
                          : tableData.reduce((s, e) => s + (e.plannedDayShift ?? e.planned ?? 0), 0);
                        const actualTotal = dashboardMetrics?.personnel?.entriesList?.length > 0
                          ? toDisplayValue(dashboardMetrics?.personnel?.actual)
                          : tableData.reduce((s, e) => s + (e.actualDayShift ?? e.actual ?? 0), 0);
                        const totalVal = dashboardMetrics?.personnel?.entriesList?.length > 0
                          ? toDisplayValue(dashboardMetrics?.personnel?.total)
                          : (plannedTotal + actualTotal);
                        return (
                          <>
                            <div style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '8px',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              overflow: 'hidden',
                              marginBottom: '20px'
                            }}>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '2fr 1fr 1fr',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                padding: '12px 16px',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                              }}>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>Role</div>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600, textAlign: 'center' }}>Planned (hrs)</div>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600, textAlign: 'center' }}>Actual (hrs)</div>
                              </div>
                              {tableData.map((entry, index) => (
                                <div
                                  key={entry.id ?? entry.keyName ?? index}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: '2fr 1fr 1fr',
                                    padding: '12px 16px',
                                    borderBottom: index < tableData.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                                    backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent'
                                  }}
                                >
                                  <div style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '14px', fontWeight: 400 }}>
                                    {entry.name || entry.keyName}
                                  </div>
                                  <div style={{ color: '#fff', fontSize: '14px', fontWeight: 400, textAlign: 'center' }}>
                                    {bothShift ? (toDisplayValue(entry.plannedDayShift) + toDisplayValue(entry.plannedNightShift)) : (shift === 'day' ? toDisplayValue(entry.plannedDayShift) : toDisplayValue(entry.plannedNightShift))}
                                  </div>
                                  <div style={{ color: '#fff', fontSize: '14px', fontWeight: 400, textAlign: 'center' }}>
                                    {bothShift ? (toDisplayValue(entry.actualDayShift) + toDisplayValue(entry.actualNightShift)) : (shift === 'day' ? toDisplayValue(entry.actualDayShift) : toDisplayValue(entry.actualNightShift))}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div style={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              gap: '24px',
                              padding: '12px 0',
                              borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <div><span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px' }}>Planned:</span> <span style={{ color: '#fff', fontWeight: 600 }}>{plannedTotal} hrs</span></div>
                              <div><span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px' }}>Actual:</span> <span style={{ color: '#fff', fontWeight: 600 }}>{actualTotal} hrs</span></div>
                              <div><span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px' }}>Total:</span> <span style={{ color: '#fff', fontWeight: 600 }}>{totalVal} hrs</span></div>
                            </div>
                          </>
                        );
                      })()}
                    </Col>}
                  </Row>
                </div>
              ) : item.title === 'Consumables' ? (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  {/* Consumables Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(82, 196, 26, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <ShoppingOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>CONSUMABLES</Title>
                    <div style={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '11px', marginTop: '2px' }}>Data from: Consumables Entry · Trend: how much consumed vs remaining</div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                      <ViewToggleBtn cardKey="section_consumables_view" />
                      <ExpandBtn cardKey={`section_${item.key}`} />
                    </div>
                  </div>

                  {(() => {
                    const sampleTableData = [
                      { consumableName: 'Welding Electrode E7018', productCode: 'WE-7018', unit: 'kg', price: 4.50, usedTodayDayShift: 45, usedTodayNightShift: 38, toDateValue: 850, remaining: 2150 },
                      { consumableName: 'Flux Core Wire', productCode: 'FC-71T', unit: 'kg', price: 8.20, usedTodayDayShift: 12, usedTodayNightShift: 10, toDateValue: 220, remaining: 780 },
                      { consumableName: 'Shielding Gas (Argon)', productCode: 'SG-AR', unit: 'm³', price: 12.00, usedTodayDayShift: 8, usedTodayNightShift: 6, toDateValue: 180, remaining: 820 },
                      { consumableName: 'Contact Tips', productCode: 'CT-35', unit: 'pcs', price: 1.80, usedTodayDayShift: 4, usedTodayNightShift: 3, toDateValue: 95, remaining: 405 },
                      { consumableName: 'Grinding Discs', productCode: 'GD-4.5', unit: 'pcs', price: 2.50, usedTodayDayShift: 6, usedTodayNightShift: 5, toDateValue: 105, remaining: 395 }
                    ];
                    const entriesList = dashboardMetrics?.consumables?.entriesList && dashboardMetrics.consumables.entriesList.length > 0
                      ? dashboardMetrics.consumables.entriesList
                      : sampleTableData;
                    const cost = dashboardMetrics?.consumables?.totalCost ?? dashboardMetrics?.consumables?.cost;
                    const costDisplay = cost != null && cost !== ''
                      ? (() => { const n = Number(toDisplayValue(cost)); return !Number.isNaN(n) ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `$${toDisplayValue(cost)}`; })()
                      : '$12,450.00';
                    const BAR_COLORS = ['#1890ff', '#52c41a', '#faad14', '#722ed1', '#ff4d4f', '#13c2c2', '#ff1493'];
                    return (
                      <Row gutter={[16, 16]}>
                        {/* Chart: Cost consumed per item */}
                        {showChartFor(item.key, 'section_consumables_view') && <Col xs={24} lg={fsColSpan(item.key)}>
                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Cost consumed per item ($)</div>
                            <ResponsiveContainer width="100%" height={260}>
                              <BarChart data={entriesList.map(e => ({
                                name: (e.consumableName || '').length > 18 ? (e.consumableName || '').slice(0, 16) + '…' : (e.consumableName || '-'),
                                fullName: e.consumableName || '-',
                                qty: toDisplayValue(e.toDateValue ?? 0),
                                unit: e.unit || '',
                                unitPrice: Number(e.price ?? 0),
                                totalCost: Math.round(toDisplayValue(e.toDateValue ?? 0) * Number(e.price ?? 0) * 100) / 100
                              }))} margin={{ top: 5, right: 20, left: 10, bottom: 55 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                <XAxis dataKey="name" stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} angle={-30} textAnchor="end" height={65} />
                                <YAxis stroke="rgba(255,255,255,0.65)" style={{ fontSize: '11px' }} tickFormatter={(v) => `$${v}`} />
                                <Tooltip
                                  contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }}
                                  content={({ active, payload }) => {
                                    if (!active || !payload?.length) return null;
                                    const d = payload[0]?.payload || {};
                                    return (
                                      <div style={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#fff' }}>
                                        <div style={{ fontWeight: 600, marginBottom: '6px' }}>{d.fullName}</div>
                                        <div style={{ color: '#e8e8e8' }}>Consumed: {d.qty} {d.unit}</div>
                                        <div style={{ color: '#faad14' }}>Unit price: ${Number(d.unitPrice).toFixed(2)}/{d.unit || 'unit'}</div>
                                        <div style={{ color: '#52c41a', fontWeight: 600 }}>Total cost: ${Number(d.totalCost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                      </div>
                                    );
                                  }}
                                />
                                <Bar dataKey="totalCost" name="Cost ($)" radius={[4, 4, 0, 0]}>
                                  {entriesList.map((_, idx) => <Cell key={`c-cost-${idx}`} fill={BAR_COLORS[idx % BAR_COLORS.length]} />)}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </Col>}

                        {/* Table: with price, unit, total cost */}
                        {showTableFor(item.key, 'section_consumables_view') && <Col xs={24} lg={fsColSpan(item.key)}>
                          {/* Cost of Consumables – primary focus */}
                          <div style={{
                            marginBottom: '20px',
                            padding: '20px 24px',
                            background: 'linear-gradient(135deg, rgba(82, 196, 26, 0.25) 0%, rgba(82, 196, 26, 0.08) 100%)',
                            border: '2px solid rgba(82, 196, 26, 0.6)',
                            borderRadius: '12px',
                            boxShadow: '0 4px 16px rgba(82, 196, 26, 0.2)',
                            textAlign: 'center'
                          }}>
                            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 600, marginBottom: '8px', letterSpacing: '0.02em' }}>COST OF CONSUMABLES</div>
                            <div style={{ color: '#52c41a', fontSize: '32px', fontWeight: 800, letterSpacing: '0.02em' }}>{costDisplay}</div>
                            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginTop: '4px' }}>Total value consumed to date</div>
                          </div>
                          <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', marginBottom: '20px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 0.7fr 0.8fr 0.8fr 1fr', backgroundColor: 'rgba(255,255,255,0.1)', padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                              <div style={{ color: '#fff', fontSize: '12px', fontWeight: 600 }}>Name</div>
                              <div style={{ color: '#fff', fontSize: '12px', fontWeight: 600, textAlign: 'center' }}>Unit</div>
                              <div style={{ color: '#fff', fontSize: '12px', fontWeight: 600, textAlign: 'center' }}>Price/Unit ($)</div>
                              <div style={{ color: '#fff', fontSize: '12px', fontWeight: 600, textAlign: 'center' }}>Consumed</div>
                              <div style={{ color: '#fff', fontSize: '12px', fontWeight: 600, textAlign: 'right' }}>Total Cost ($)</div>
                            </div>
                            {entriesList.map((entry, index) => {
                              const qty = toDisplayValue(entry.toDateValue ?? 0);
                              const unitPrice = Number(entry.price ?? 0);
                              const totalCost = qty * unitPrice;
                              return (
                                <div key={entry.id || index} style={{ display: 'grid', gridTemplateColumns: '2fr 0.7fr 0.8fr 0.8fr 1fr', padding: '10px 12px', borderBottom: index < entriesList.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none', backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '12px' }}>{entry.consumableName || '-'}</div>
                                  <div style={{ color: '#fff', fontSize: '12px', textAlign: 'center' }}>{entry.unit || '-'}</div>
                                  <div style={{ color: '#faad14', fontSize: '12px', textAlign: 'center' }}>${unitPrice.toFixed(2)}</div>
                                  <div style={{ color: '#1890ff', fontSize: '12px', textAlign: 'center' }}>{qty} {entry.unit || ''}</div>
                                  <div style={{ color: '#52c41a', fontSize: '12px', textAlign: 'right', fontWeight: 600 }}>${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                </div>
                              );
                            })}
                          </div>
                          {/* Summary Totals */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                              <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px' }}>Total Items</div>
                              <div style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>{toDisplayValue(dashboardMetrics?.consumables?.totalItems ?? entriesList.length)}</div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                              <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px' }}>Low Stock</div>
                              <div style={{ color: dashboardMetrics?.consumables?.lowStock > 0 ? '#faad14' : '#fff', fontSize: '16px', fontWeight: 600 }}>{toDisplayValue(dashboardMetrics?.consumables?.lowStock ?? 1)}</div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
                              <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px' }}>Total Remaining</div>
                              <div style={{ color: '#52c41a', fontSize: '16px', fontWeight: 600 }}>
                                {dashboardMetrics?.consumables?.totalRemaining != null
                                  ? toDisplayValue(dashboardMetrics.consumables.totalRemaining)
                                  : toDisplayValue(entriesList.reduce((sum, e) => sum + Number(e.remaining ?? e.remainingStock ?? 0), 0))}
                              </div>
                            </div>
                          </div>
                        </Col>}
                      </Row>
                    );
                  })()}
                </div>
              ) : item.title === 'Production Impact' ? (
                <div>
                  {/* Production Impact Header */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(250, 173, 20, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <WarningOutlined style={{ color: '#faad14', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>
                      Production Impact
                    </Title>
                    <div style={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '11px', marginTop: '2px' }}>Data from: Downtime Entry (responsibility split, delays)</div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                      <ViewToggleBtn cardKey="section_prodImpact_view" />
                      <ExpandBtn cardKey={`section_${item.key}`} />
                    </div>
                  </div>

                  {/* 50/50 Layout: Donut Chart (left) + Detail Table + Summary (right) */}
                  <Row gutter={[16, 16]}>
                    {/* Left: Donut PieChart - downtime hours by responsibility */}
                    {showChartFor(item.key, 'section_prodImpact_view') && <Col xs={24} lg={fsColSpan(item.key)}>
                      {(() => {
                        const PIE_COLORS = ['#ff4d4f', '#faad14', '#1890ff', '#52c41a', '#722ed1', '#ff1493', '#13c2c2'];
                        const samplePieData = [
                          { name: 'Customer', value: 4.5 },
                          { name: 'Contractor', value: 2.0 },
                          { name: 'Equipment', value: 3.5 },
                          { name: 'Environment', value: 1.5 },
                          { name: 'Safety', value: 0.5 }
                        ];
                        let pieData = samplePieData;
                        if (dashboardMetrics?.productionImpact?.entriesList && dashboardMetrics.productionImpact.entriesList.length > 0) {
                          const categoryMap = {};
                          dashboardMetrics.productionImpact.entriesList.forEach(entry => {
                            if (entry.allocations && entry.allocations.length > 0) {
                              entry.allocations.forEach(a => {
                                const cat = a.category || 'Unallocated';
                                categoryMap[cat] = (categoryMap[cat] || 0) + (Number(a.hours) || 0);
                              });
                            } else {
                              const cat = entry.categoryTitle || 'Other';
                              categoryMap[cat] = (categoryMap[cat] || 0) + (Number(entry.totalHours) || 0);
                            }
                          });
                          pieData = Object.entries(categoryMap).map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }));
                        }
                        const totalHrs = pieData.reduce((s, d) => s + d.value, 0);
                        return (
                          <div style={{ marginBottom: '20px' }}>
                            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Downtime hours by responsibility</div>
                            <ResponsiveContainer width="100%" height={260}>
                              <PieChart>
                                <Pie data={pieData} innerRadius={60} outerRadius={95} paddingAngle={2} dataKey="value" nameKey="name">
                                  {pieData.map((entry, idx) => <Cell key={`pi-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} />)}
                                </Pie>
                                <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff' }} formatter={(val) => `${val}h`} />
                                <Legend wrapperStyle={{ color: '#fff' }} />
                                <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle" style={{ fill: '#fff', fontSize: '20px', fontWeight: 700 }}>{totalHrs.toFixed(1)}h</text>
                                <text x="50%" y="56%" textAnchor="middle" dominantBaseline="middle" style={{ fill: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>Total downtime</text>
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        );
                      })()}
                    </Col>}

                    {/* Right: Detail table + Summary totals */}
                    {showTableFor(item.key, 'section_prodImpact_view') && <Col xs={24} lg={fsColSpan(item.key)}>
                      {(() => {
                        const sampleTableData = [
                          { categoryTitle: 'Inspection Hold / QC Clearance', reason: 'Waiting for QC', startTime: '07:45', endTime: '12:15', totalHours: 4.5, allocations: [{ category: 'Customer', subCategory: 'QC Hold', hours: 4.5 }] },
                          { categoryTitle: 'Equipment Breakdown', reason: 'Welding machine failure', startTime: '14:00', endTime: '17:30', totalHours: 3.5, allocations: [{ category: 'Equipment', subCategory: 'Machine Breakdown', hours: 3.5 }] },
                          { categoryTitle: 'Safety Hold', reason: 'Permit not available', startTime: '09:00', endTime: '09:30', totalHours: 0.5, allocations: [{ category: 'Safety', subCategory: 'Permit', hours: 0.5 }] },
                          { categoryTitle: 'Weather Delay', reason: 'High wind', startTime: '11:00', endTime: '12:30', totalHours: 1.5, allocations: [{ category: 'Environment', subCategory: 'Wind', hours: 1.5 }] },
                          { categoryTitle: 'Re-welding Due to Defect', reason: 'Repair work', startTime: '15:00', endTime: '17:00', totalHours: 2.0, allocations: [{ category: 'Contractor', subCategory: 'Rework', hours: 2.0 }] }
                        ];
                        const tableData = dashboardMetrics?.productionImpact?.entriesList?.length > 0
                          ? dashboardMetrics.productionImpact.entriesList
                          : sampleTableData;
                        const incidentCount = dashboardMetrics?.productionImpact?.incidents != null ? toDisplayValue(dashboardMetrics.productionImpact.incidents) : tableData.length;
                        const totalHoursVal = dashboardMetrics?.productionImpact?.totalHours != null ? toDisplayValue(dashboardMetrics.productionImpact.totalHours) : tableData.reduce((s, e) => s + (Number(e.totalHours) || 0), 0);
                        const allocatedVal = dashboardMetrics?.productionImpact?.allocated != null ? toDisplayValue(dashboardMetrics.productionImpact.allocated) : tableData.reduce((s, e) => s + (Number(e.totalHours) || 0), 0);
                        return (
                          <>
                            <div style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '8px',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              overflow: 'hidden',
                              marginBottom: '16px'
                            }}>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1.5fr 1.2fr 0.8fr',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                padding: '12px 16px',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                              }}>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>Category</div>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600, textAlign: 'center' }}>Reason</div>
                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600, textAlign: 'center' }}>Hours</div>
                              </div>
                              {tableData.map((entry, index) => (
                                <div
                                  key={entry.id || index}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1.5fr 1.2fr 0.8fr',
                                    padding: '12px 16px',
                                    borderBottom: index < tableData.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                                    backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent',
                                    alignItems: 'center'
                                  }}
                                >
                                  <div style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '14px', fontWeight: 400 }}>{entry.categoryTitle || '-'}</div>
                                  <div style={{ color: '#fff', fontSize: '14px', fontWeight: 400, textAlign: 'center' }}>{entry.reason || '-'}</div>
                                  <div style={{ color: '#fff', fontSize: '14px', fontWeight: 400, textAlign: 'center' }}>{toDisplayValue(entry.totalHours)}</div>
                                </div>
                              ))}
                            </div>
                            {/* Summary: Incidents, Total Hours, Allocated */}
                            <Row gutter={[12, 12]}>
                              <Col span={8}>
                                <div style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  padding: '12px',
                                  textAlign: 'center'
                                }}>
                                  <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '12px', marginBottom: '4px' }}>Incidents</div>
                                  <div style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>{incidentCount}</div>
                                </div>
                              </Col>
                              <Col span={8}>
                                <div style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  padding: '12px',
                                  textAlign: 'center'
                                }}>
                                  <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '12px', marginBottom: '4px' }}>Total Hours</div>
                                  <div style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>{totalHoursVal}</div>
                                </div>
                              </Col>
                              <Col span={8}>
                                <div style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  padding: '12px',
                                  textAlign: 'center'
                                }}>
                                  <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '12px', marginBottom: '4px' }}>Allocated</div>
                                  <div style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>{allocatedVal}</div>
                                </div>
                              </Col>
                            </Row>
                          </>
                        );
                      })()}
                    </Col>}
                  </Row>
                </div>
              ) : (
                <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '14px' }}>
                  {item.title} content will be displayed here.
                </div>
              )}
            </Card>
              </Col>
        ))}
          </Row>

                {/* Defect Trend – at end of dashboard (uses same date range as filters) */}
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', marginTop: '24px' }}>Defect trend</div>
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24}>
                    <DefectTrendChart
                      data={dashboardMetrics?.defectTrends || []}
                      dataSource="Quality Entry & Weld Log (Defect Type)"
                      dateRangeLabel={effectiveDateRange?.[0] && effectiveDateRange?.[1]
                        ? `${moment(effectiveDateRange[0]).format('DD MMM YYYY')} – ${moment(effectiveDateRange[1]).format('DD MMM YYYY')}`
                        : (dateRangePreset === 'week' ? 'Week' : dateRangePreset === 'month' ? 'Month' : dateRangePreset === 'year' ? 'Year' : 'Custom')}
                    />
            </Col>
          </Row>
        </>
      )}
        </>)}

      {/* Fullscreen backdrop – click to dismiss */}
      {fullscreenCard && (
        <div
          onClick={() => setFullscreenCard(null)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 999 }}
        />
      )}
    </div>
  );
};

export default DashboardLog;

